import { useLiveQuery } from 'dexie-react-hooks';
import { useEffect } from 'react';
import { create } from 'zustand';

import { settings } from '@/lib/db';

import type { AutoImportDeps, AutoImportRun } from './auto-import';
import type { AutoImportConfig } from './settings';
import { AUTO_IMPORT_KEYS, readAutoImportConfig } from './settings';

/**
 * The startup check as a store rather than component state.
 *
 * It has to run exactly once per page load, whatever route the pilot landed
 * on and however many components care about the answer — and `StrictMode`
 * mounts every effect twice in development, which a `useRef` guard inside one
 * component does not survive. `check()` returns early unless the store is
 * idle, so the second mount, a second banner, and a remount after a
 * navigation all reuse the first run's result.
 *
 * Same shape as `features/import/queue.ts` for the same reasons, `reset()`
 * included: a module-level singleton needs a way back to its initial state
 * between tests.
 */

export interface AutoImportState {
  status: 'idle' | 'checking' | 'done';
  run: AutoImportRun | undefined;
  /** Whether the pilot has waved the last run's banner away. */
  dismissed: boolean;
  /** Runs the check unless one has already run this page load. `force` re-runs it. */
  check: (deps?: AutoImportDeps) => Promise<void>;
  dismiss: () => void;
  reset: () => void;
}

export const useAutoImportStore = create<AutoImportState>((set, get) => ({
  status: 'idle',
  run: undefined,
  dismissed: false,

  check: async (deps = {}) => {
    const { status } = get();
    if (status === 'checking' || (status === 'done' && deps.force !== true)) {
      return;
    }
    set({ status: 'checking', dismissed: false });
    let run: AutoImportRun;
    try {
      /*
       * Loaded on use, not at startup. The runner pulls in every source adapter
       * and the duplicate check, which is dead weight on the first paint of an
       * app where this feature is off by default — and not needed even when it
       * is on, until after the page the pilot asked for is on screen.
       */
      const { runAutoImport } = await import('./auto-import');
      run = await runAutoImport(deps);
    } catch (cause) {
      // `runAutoImport` turns a source failure into a result rather than
      // throwing, so reaching here means the database itself did — worth
      // saying, and never worth breaking the app the pilot came to use.
      run = {
        status: 'failed',
        found: [],
        message:
          cause instanceof Error
            ? `The startup check could not run: ${cause.message}`
            : 'The startup check could not run.',
        checkedAt: new Date().toISOString(),
      };
    }
    set({ status: 'done', run });
  },

  dismiss: () => {
    set({ dismissed: true });
  },

  reset: () => {
    set({ status: 'idle', run: undefined, dismissed: false });
  },
}));

/** Kicks the once-per-load check off. Mounted once, in the root layout. */
export function useStartupAutoImport(): void {
  const check = useAutoImportStore((state) => state.check);
  useEffect(() => {
    void check();
  }, [check]);
}

/** The stored configuration, live — `null` while the database is still opening. */
export function useAutoImportConfig(): AutoImportConfig | null {
  const rows = useLiveQuery(() => settings.getAll(), [], null);
  return rows === null ? null : readAutoImportConfig(rows);
}

/**
 * Sets the default aircraft.
 *
 * Clearing it leaves `enabled` alone rather than switching it off: a pilot
 * swapping aeroplanes between two selections has not changed their mind about
 * wanting the check, and `runAutoImport` skips harmlessly while there is
 * nothing to check.
 */
export function setDefaultAircraft(aircraftId: string | undefined): Promise<void> {
  return settings.set(AUTO_IMPORT_KEYS.aircraftId, aircraftId ?? '');
}

export function setAutoImportEnabled(enabled: boolean): Promise<void> {
  return settings.set(AUTO_IMPORT_KEYS.enabled, enabled);
}
